import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from '@emotion/styled';
import { trackPageView } from '../services/pageView';

const Nav = styled.header`
  position: sticky;
  top: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 ${({ theme }) => theme.spacing(8)};
  height: 56px;
  background: ${({ theme }) => theme.background.primary};
  border-bottom: 1px solid ${({ theme }) => theme.border.color.light};
`;

const NavBrand = styled(Link)`
  font-size: ${({ theme }) => theme.font.size.lg};
  font-weight: ${({ theme }) => theme.font.weight.semiBold};
  color: ${({ theme }) => theme.font.color.primary};
  text-decoration: none;
  letter-spacing: -0.3px;
`;

const NavLinks = styled.nav`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(6)};

  @media (max-width: 640px) {
    gap: ${({ theme }) => theme.spacing(4)};
  }
`;

const NavLink = styled(Link)<{ active?: boolean }>`
  font-size: ${({ theme }) => theme.font.size.sm};
  font-weight: ${({ theme }) => theme.font.weight.medium};
  color: ${({ theme, active }) => active ? theme.font.color.primary : theme.font.color.secondary};
  text-decoration: none;
  transition: color 0.1s ease;

  &:hover {
    color: ${({ theme }) => theme.font.color.primary};
  }
`;

const SignInButton = styled(Link)`
  height: 32px;
  padding: 0 ${({ theme }) => theme.spacing(4)};
  background: ${({ theme }) => theme.accent.primary};
  color: ${({ theme }) => theme.font.color.inverted};
  border-radius: ${({ theme }) => theme.border.radius.sm};
  font-size: ${({ theme }) => theme.font.size.sm};
  font-weight: ${({ theme }) => theme.font.weight.medium};
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  transition: background 0.1s ease;

  &:hover {
    background: ${({ theme }) => theme.accent.accent10};
  }
`;

const Main = styled.main`
  min-height: calc(100vh - 56px - 60px);
`;

const Footer = styled.footer`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 ${({ theme }) => theme.spacing(8)};
  height: 60px;
  border-top: 1px solid ${({ theme }) => theme.border.color.light};
  background: ${({ theme }) => theme.background.primary};

  @media (max-width: 640px) {
    flex-direction: column;
    height: auto;
    padding: ${({ theme }) => theme.spacing(4)} ${({ theme }) => theme.spacing(6)};
    gap: ${({ theme }) => theme.spacing(2)};
    text-align: center;
  }
`;

const FooterCopy = styled.span`
  font-size: ${({ theme }) => theme.font.size.xs};
  color: ${({ theme }) => theme.font.color.tertiary};
`;

const FooterLinks = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(5)};
`;

const FooterLink = styled(Link)`
  font-size: ${({ theme }) => theme.font.size.xs};
  color: ${({ theme }) => theme.font.color.tertiary};
  text-decoration: none;

  &:hover {
    color: ${({ theme }) => theme.font.color.secondary};
  }
`;

interface PublicLayoutProps {
  children: React.ReactNode;
}

export function PublicLayout({ children }: PublicLayoutProps) {
  const location = useLocation();

  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);

  return (
    <>
      <Nav>
        <NavBrand to="/">Simkura</NavBrand>
        <NavLinks>
          <NavLink to="/hardware" active={location.pathname === '/hardware'}>Hardware providers</NavLink>
          <NavLink to="/developers" active={location.pathname === '/developers'}>Software Integrations</NavLink>
          <NavLink to="/open-source" active={location.pathname === '/open-source'}>Open Source</NavLink>
          <NavLink to="/about" active={location.pathname === '/about'}>About</NavLink>
          <NavLink to="/contact" active={location.pathname === '/contact'}>Contact</NavLink>
        </NavLinks>
        <SignInButton to="/login">Sign in</SignInButton>
      </Nav>
      <Main>{children}</Main>
      <Footer>
        <FooterCopy>© {new Date().getFullYear()} Simkura. All rights reserved.</FooterCopy>
        <FooterLinks>
          <FooterLink to="/hardware">Hardware providers</FooterLink>
          <FooterLink to="/developers">Software Integrations</FooterLink>
          <FooterLink to="/open-source">Open Source</FooterLink>
          <FooterLink to="/about">About</FooterLink>
          <FooterLink to="/contact">Contact</FooterLink>
          <FooterLink to="/privacy">Privacy & Terms</FooterLink>
        </FooterLinks>
      </Footer>
    </>
  );
}

export default PublicLayout;
